import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useGetAuthApi, usePatchAuthApi } from '@/hooks/UseApiCalls';
import editIc from '@/assets/icons/edit.svg';
import deleteIc from '@/assets/icons/delete.svg';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { DonationCurrency, DonationPageConfig, QuickDonationButton } from '../PublicDonation/types';
import QuickButtonDialog from './QuickButtonDialog';
import styles from './DonationsAdmin.module.css';

interface Props {
  fundId: number;
}

const MAX_PER_CURRENCY = 6;

const QuickButtonsEditor = ({ fundId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const url = `donations/page-config/${fundId}`;
  const { data } = useGetAuthApi<DonationPageConfig>(url);
  const allButtons = data?.quickButtons ?? [];

  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [creatingCurrency, setCreatingCurrency] = useState<DonationCurrency | null>(null);
  const [confirmingRemoveIdx, setConfirmingRemoveIdx] = useState<number | null>(null);

  const patch = usePatchAuthApi<void, { quickButtons: QuickDonationButton[] }>(url);
  const busy = patch.isPending;

  const persist = (next: QuickDonationButton[], onDone: () => void) => {
    patch.mutate(
      { quickButtons: next },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [url] });
          onDone();
        },
      }
    );
  };

  const handleCreate = (btn: QuickDonationButton) => {
    persist([...allButtons, btn], () => setCreatingCurrency(null));
  };

  const handleEdit = (btn: QuickDonationButton) => {
    if (editingIdx === null) return;
    persist(
      allButtons.map((b, i) => (i === editingIdx ? btn : b)),
      () => setEditingIdx(null)
    );
  };

  const confirmRemove = () => {
    if (confirmingRemoveIdx === null) return;
    const idx = confirmingRemoveIdx;
    persist(
      allButtons.filter((_, i) => i !== idx),
      () => setConfirmingRemoveIdx(null)
    );
  };

  const buttonsFor = (currency: DonationCurrency) =>
    allButtons
      .map((b, originalIdx) => ({ ...b, originalIdx }))
      .filter((b) => (b.currency || 'ILS') === currency)
      .sort((a, b) => a.amount - b.amount);

  const renderCurrencySection = (currency: DonationCurrency, symbol: string, label: string) => {
    const items = buttonsFor(currency);
    const canAdd = items.length < MAX_PER_CURRENCY;

    return (
      <div className={styles.currencySection}>
        <h3 className={styles.currencySectionTitle}>{symbol} {label}</h3>
        <div className={styles.previewGrid}>
          {items.map((btn) => {
            const isRemoving = busy && confirmingRemoveIdx === btn.originalIdx;
            return (
              <div key={btn.originalIdx} className={`${styles.previewCard} ${isRemoving ? styles.previewCardBusy : ''}`}>
                <div className={styles.previewCardContent}>
                  <span className={styles.previewAmount} dir="ltr">
                    {symbol}{btn.amount.toLocaleString()}
                    {(btn.months ?? 1) > 1 && ` x ${btn.months}`}
                  </span>
                  {btn.label && <span className={styles.previewLabel}>{btn.label}</span>}
                </div>
                <div className={styles.previewCardActions}>
                  <button type="button" className={styles.iconBtn} onClick={() => setEditingIdx(btn.originalIdx)} disabled={busy}>
                    <img src={editIc} alt="" />
                  </button>
                  <button type="button" className={styles.iconBtn} onClick={() => setConfirmingRemoveIdx(btn.originalIdx)} disabled={busy}>
                    <img src={deleteIc} alt="" />
                  </button>
                </div>
              </div>
            );
          })}
          {canAdd && (
            <button type="button" className={styles.addCard} onClick={() => setCreatingCurrency(currency)} disabled={busy}>
              <span className={styles.addCardPlus}>+</span>
              <span className={styles.addCardLabel}>{t('donations.admin.addButton')}</span>
            </button>
          )}
        </div>
        {items.length === 0 && !canAdd && <p className={styles.empty}>{t('donations.admin.noButtons')}</p>}
      </div>
    );
  };

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('donations.admin.quickButtonsTitle')}</h2>
      <p className={styles.panelSubtitle}>{t('donations.admin.quickButtonsSubtitle')}</p>

      {renderCurrencySection('ILS', '₪', t('donations.ils'))}
      {renderCurrencySection('USD', '$', t('donations.usd'))}

      <QuickButtonDialog
        open={creatingCurrency !== null}
        onOpenChange={(o) => { if (!o) setCreatingCurrency(null); }}
        initial={creatingCurrency ? { amount: 0, label: '', currency: creatingCurrency } : null}
        isNew
        currencySymbol={creatingCurrency === 'USD' ? '$' : '₪'}
        isPending={busy && creatingCurrency !== null}
        onSave={handleCreate}
      />
      <QuickButtonDialog
        open={editingIdx !== null}
        onOpenChange={(o) => { if (!o) setEditingIdx(null); }}
        initial={editingIdx !== null ? allButtons[editingIdx] : null}
        currencySymbol={editingIdx !== null && allButtons[editingIdx]?.currency === 'USD' ? '$' : '₪'}
        isPending={busy && editingIdx !== null}
        onSave={handleEdit}
      />

      <ConfirmationDialog
        open={confirmingRemoveIdx !== null}
        onOpenChange={(o) => { if (!o && !busy) setConfirmingRemoveIdx(null); }}
        title={t('donations.admin.confirmRemoveButtonTitle')}
        message={t('donations.admin.confirmRemoveButtonMessage')}
        confirmText="donations.admin.removeButton"
        cancelText="cancel"
        variant="danger"
        isLoading={busy && confirmingRemoveIdx !== null}
        onConfirm={confirmRemove}
      />
    </div>
  );
};

export default QuickButtonsEditor;
